import { computed, onMounted, ref, watch, watchEffect, toRaw } from 'vue';
import { useCollaboratorProfiles } from '@/composables/api/useCollaboratorProfiles';
import { erpClient } from '@tfpl/api-client';
import { useConfig } from '@/composables/useConfig';
import isEqual from 'lodash/isEqual';
import { useSnack } from '@/composables/useSnack';
import { useI18n } from 'vue-i18n';
import { useLogger } from '@/composables/useLogger';
import moment from 'moment/moment';
import { useDisplay } from '@/composables/useDisplay';
import { useAuthorization } from '@/composables/auth/useAuthorization';
import { useRoute, useRouter } from 'vue-router';
import { useActiveCompany } from '@/composables/useActiveCompany';
import { useCopyData } from '@/composables/useCopyData';
import CollaboratorProjects from '@/components/CollaboratorProfilesComponents/CollaboratorProjects.vue';
import CollaboratorCertifications from '@/components/CollaboratorProfilesComponents/CollaboratorCertifications.vue';

export const useCollaboratorProfilesPageLogic = () => {
  const { t } = useI18n();
  const { loading, fetchCollaboratorsAndProjects, collaboratorProfiles } = useCollaboratorProfiles();
  const { getConfig } = useConfig();
  const { addSnack } = useSnack();
  const { logger } = useLogger();
  const { isMobile, isMedium } = useDisplay();
  const { hasLoggedUserPermission } = useAuthorization();
  const route = useRoute();
  const router = useRouter();
  const { getActiveCompany } = useActiveCompany();
  const { getCollaboratorProfileCopyData, getCollaboratorProfileSkillsCopyData } = useCopyData();

  const isSmallScreen = computed(() => isMobile.value || isMedium.value);

  const selectedCollaboratorProfile = ref<erpClient.CollaboratorProfileOutputDto | null>(null);
  const originalCollaboratorProfile = ref<erpClient.CollaboratorProfileOutputDto | null>(null);
  const saveInProgress = ref<boolean>(false);
  const collaboratorProjectsRef = ref<InstanceType<typeof CollaboratorProjects> | null>(null);
  const collaboratorCertificationsRef = ref<InstanceType<typeof CollaboratorCertifications> | null>(null);

  // Define the selected collaborator UUID
  const selectedCollaboratorUuid = ref<string | null>(null);

  const selectedCollaboratorProfileUrl = computed({
    get: () => selectedCollaboratorProfile.value?.profileUrl || '',
    set: (value) => {
      if (selectedCollaboratorProfile.value) {
        selectedCollaboratorProfile.value.profileUrl = value === '' ? undefined : value;
      }
    },
  });

  const selectCollaboratorProfile = async (collaboratorProfile: erpClient.CollaboratorProfileOutputDto) => {
    selectedCollaboratorProfile.value = collaboratorProfile;
    // Use a safe deep clone on the raw object to avoid cloning reactive proxies/functions
    originalCollaboratorProfile.value = structuredClone(toRaw(selectedCollaboratorProfile.value));
    selectedCollaboratorUuid.value = collaboratorProfile.uuid;
    await router.push({ path: 'collaborator-profiles', query: { uuid: selectedCollaboratorUuid.value } });
  };

  watch(
    () => route.query.uuid,
    () => {
      setSelectedUserBasedOnRoute();
    },
  );

  const updateCollaboratorProfile = (collaboratorProfile: erpClient.CollaboratorProfileOutputDto) => {
    selectedCollaboratorProfile.value = collaboratorProfile;
  };

  const isSame = computed(() => {
    return (
      isEqual(selectedCollaboratorProfile.value, originalCollaboratorProfile.value) &&
      collaboratorProjectsRef.value?.isUpdated === false
    );
  });

  const updatedDate = computed(() => {
    if (!selectedCollaboratorProfile.value?.updatedAt) {
      return '-';
    }

    return moment(selectedCollaboratorProfile.value?.updatedAt).format('DD.MM.YYYY');
  });

  const skillValuesAgeDays = computed<number | null>(() => {
    const ts = selectedCollaboratorProfile.value?.skillValuesUpdatedAt;
    if (!ts) {
      return null;
    }
    return moment().diff(moment(ts), 'days');
  });

  const isSkillsOutdated = computed(() => {
    return selectedCollaboratorProfile.value?.isSkillsOutdated === true;
  });

  const goToCollaboratorSkills = () => {
    const collaborator = selectedCollaboratorProfile.value?.collaborator;
    if (!collaborator?.uuid) {
      router.push({ name: 'Collaborators skills' }).catch((e: unknown) => logger.error(e));
      return;
    }
    router
      .push({
        name: 'Collaborators skills',
        query: {
          uuid: collaborator.uuid,
          ...(collaborator.positions?.[0]?.departmentUuid
            ? { departmentUuid: collaborator.positions[0].departmentUuid }
            : {}),
        },
      })
      .catch((e: unknown) => logger.error(e));
  };

  const saveCollaboratorProfile = async () => {
    if (isSame.value) {
      return;
    }

    saveInProgress.value = true;
    const collaboratorCertUuids = selectedCollaboratorProfile?.value?.collaborator?.certifications?.map(
      (cert) => cert.uuid,
    );

    const payload = { ...(selectedCollaboratorProfile.value as erpClient.CollaboratorProfileCreateUpdateDto) };

    if (payload.collaborator) {
      payload.collaborator.certificationsUuids = collaboratorCertUuids;
    }

    try {
      await collaboratorProjectsRef.value?.saveData();

      let savedDto: erpClient.CollaboratorProfileOutputDto;
      if (originalCollaboratorProfile.value?._created) {
        const { data: result } = await erpClient.CollaboratorProfilesService.put({
          path: {
            tenant: getConfig().TENANT,
            company: getActiveCompany().code,
            uuid: selectedCollaboratorProfile.value?.uuid as string,
          },
          body: {
            ...payload,
            collaboratorUuid: selectedCollaboratorProfile.value?.collaborator.uuid as string,
          },
          throwOnError: true,
        });
        savedDto = result.data;
      } else {
        const { data: result } = await erpClient.CollaboratorProfilesService.post({
          path: {
            tenant: getConfig().TENANT,
            company: getActiveCompany().code,
          },
          body: {
            ...payload,
            collaboratorUuid: selectedCollaboratorProfile.value?.collaborator.uuid as string,
          },
          throwOnError: true,
        });
        savedDto = result.data;
      }

      await collaboratorCertificationsRef.value?.fetchData();
      const oldProfile = collaboratorProfiles.value.find(
        (profile) => profile.uuid === selectedCollaboratorUuid.value,
      ) as erpClient.CollaboratorProfileOutputDto;
      Object.assign(oldProfile, savedDto);
      await selectCollaboratorProfile(savedDto);

      addSnack({
        id: 'toast.success',
        message: t('toast.success'),
        color: 'success',
      });
    } catch (e) {
      logger.error(e);
      addSnack({
        id: 'toast.error',
        message: t('toast.error'),
      });
    } finally {
      saveInProgress.value = false;
    }
  };

  const setSelectedUserBasedOnRoute = () => {
    if (!route?.query?.uuid) {
      return;
    }

    const matchedCollaboratorProfile = collaboratorProfiles.value.find(
      (profile) => profile.uuid === route.query.uuid || profile.collaborator?.uuid === route.query.uuid,
    );
    if (matchedCollaboratorProfile) {
      selectCollaboratorProfile(matchedCollaboratorProfile).catch((e: unknown) => logger.error(e));
    }
  };

  const openProfileUrlInNewWindow = () => {
    if (selectedCollaboratorProfileUrl.value && selectedCollaboratorProfileUrl.value !== '') {
      globalThis.open(selectedCollaboratorProfileUrl.value, '_blank');
    }
  };

  const getAllCopyData = () => {
    if (!selectedCollaboratorProfile.value) {
      return {
        html: '',
        text: '',
      };
    }
    return getCollaboratorProfileCopyData(
      selectedCollaboratorProfile.value,
      collaboratorProjectsRef.value?.projects ?? [],
      collaboratorProjectsRef.value?.projectsData ?? {},
    );
  };

  const getSkillsCopyData = () => getCollaboratorProfileSkillsCopyData(selectedCollaboratorProfile.value!);

  onMounted(() => {
    void fetchCollaboratorsAndProjects();
    watchEffect(() => {
      if (collaboratorProfiles.value.length > 0 && !selectedCollaboratorProfile.value) {
        setSelectedUserBasedOnRoute();
      }
    });
  });

  return {
    t,
    loading,
    collaboratorProfiles,
    selectedCollaboratorUuid,
    selectedCollaboratorProfile,
    saveInProgress,
    isSame,
    updatedDate,
    selectedCollaboratorProfileUrl,
    collaboratorProjectsRef,
    collaboratorCertificationsRef,
    isSmallScreen,
    hasLoggedUserPermission,
    selectCollaboratorProfile,
    updateCollaboratorProfile,
    saveCollaboratorProfile,
    openProfileUrlInNewWindow,
    getAllCopyData,
    getSkillsCopyData,
    skillValuesAgeDays,
    isSkillsOutdated,
    goToCollaboratorSkills,
  };
};
